import { useTheme } from '@shopify/restyle';
import { IconCalendar, IconList } from '@tabler/icons-react-native';
import { EmptyGraphic } from 'app/components/empty-graphic';
import { LoanCard } from 'app/components/loan-card';
import { Tabs } from 'app/components/tabs';
import { Column } from 'app/design/primitives/box';
import { ListPage } from 'app/design/primitives/page/list';
import { startOfMonth } from 'app/lib/date';
import type { Theme } from 'app/shopify';
import type { Loan, LoanType } from 'app/types/loan';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator } from 'react-native';
import Animated, { FadeIn, FadeOut, LinearTransition } from 'react-native-reanimated';
import { loanIntersectsMonth } from './loan-calendar-utils';
import { LoanItemSeparator } from './loan-item-separator';
import { ScreenHeading } from './screen-heading';
import { MobileCalendar } from './mobile-calendar';

type MobileTab = 'loans' | 'calendar';

interface ScreenContentProps {
  isLoading: boolean;
  isRefetching: boolean;
  loanType: LoanType;
  loans: Loan[];
  onRefresh: () => void;
  tPrefix: string;
}

export function MobileLoans({ isLoading, isRefetching, loanType, loans, onRefresh, tPrefix }: ScreenContentProps) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  const [activeTab, setActiveTab] = useState<MobileTab>('loans');
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const monthLoanCount = loans.filter((loan) => loanIntersectsMonth(loan, currentMonth)).length;
  const tabs = [
    {
      extraLabel: loans.length.toString(),
      label: t(`calendar.tabs.${loanType}`),
      prefix: (
        <IconList
          color={theme.colors[activeTab === 'loans' ? 'text.inverse.primary' : 'text.secondary']}
          size={18}
          strokeWidth={ICON_STROKE_WIDTH}
        />
      ),
      value: 'loans',
    },
    {
      extraLabel: monthLoanCount.toString(),
      label: t('calendar.tabs.calendar'),
      prefix: (
        <IconCalendar
          color={theme.colors[activeTab === 'calendar' ? 'text.inverse.primary' : 'text.secondary']}
          size={18}
          strokeWidth={ICON_STROKE_WIDTH}
        />
      ),
      value: 'calendar',
    },
  ];

  return (
    <Column flex={1} minHeight={0}>
      <Column gap="spacing.general.x4" paddingTop="spacing.general.x4">
        <ScreenHeading loanType={loanType} tPrefix={tPrefix} />
        <Tabs
          onPress={(tab) => {
            if (tab === 'loans' || tab === 'calendar') {
              setActiveTab(tab);
            }
          }}
          tabs={tabs}
          value={activeTab}
          variant="pill"
        />
      </Column>

      {activeTab === 'loans' ? (
        <Animated.View
          entering={FadeIn.duration(300)}
          exiting={FadeOut.duration(300)}
          key="loans"
          layout={LinearTransition.duration(300)}
          style={{ flex: 1, minHeight: 0 }}
        >
          <ListPage
            ItemSeparatorComponent={LoanItemSeparator}
            ListEmptyComponent={
              isLoading ? (
                <ActivityIndicator accessibilityLabel={t('calendar.loading')} />
              ) : (
                <EmptyGraphic
                  invertTexts
                  primary={t(`${tPrefix}${loanType}.empty.secondLine`)}
                  secondary={t(`${tPrefix}${loanType}.empty.firstLine`)}
                />
              )
            }
            contentContainerStyle={{ paddingTop: 16 }}
            data={loans}
            keyExtractor={(item) => item.id}
            onRefresh={onRefresh}
            refreshing={isRefetching}
            renderItem={({ item }) => <LoanCard {...item} />}
          />
        </Animated.View>
      ) : (
        <Animated.View
          entering={FadeIn.duration(300)}
          exiting={FadeOut.duration(300)}
          key="calendar"
          layout={LinearTransition.duration(300)}
          style={{ flex: 1, minHeight: 0 }}
        >
          <MobileCalendar currentMonth={currentMonth} loans={loans} onMonthChange={setCurrentMonth} />
        </Animated.View>
      )}
    </Column>
  );
}
